import React from 'react';

export const VinCentrixLogo = ({ size = 40, showText = false, className = '' }) => {
  const scale = size / 240; // Base size is 240px
  
  return (
    <div className={`vincentrix-logo ${className}`} style={{ display: 'flex', alignItems: 'center', gap: size * 0.3 }}>
      {/* Logo Mark */}
      <div style={{
        width: size,
        height: size,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {/* Core glow */}
        <div style={{
          position: 'absolute',
          width: size * 1.5,
          height: size * 1.5,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.3) 0%, transparent 50%)',
          filter: 'blur(20px)',
          animation: 'vcCoreBreath 4s ease-in-out infinite',
        }} />

        {/* Outer ring */}
        <div style={{
          position: 'absolute',
          width: size,
          height: size,
          borderRadius: '50%',
          background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.02))',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 0 40px rgba(59, 130, 246, 0.2), inset 0 0 30px rgba(59, 130, 246, 0.05)',
        }} />

        {/* Tech ring with scanner */}
        <div style={{
          position: 'absolute',
          width: size * 0.9,
          height: size * 0.9,
          borderRadius: '50%',
          border: '1px solid rgba(59, 130, 246, 0.3)',
        }}>
          <div style={{
            position: 'absolute',
            inset: '-1px',
            borderRadius: '50%',
            background: 'conic-gradient(from 0deg, transparent 0deg, rgba(59, 130, 246, 0.6) 20deg, transparent 40deg, transparent 180deg, rgba(59, 130, 246, 0.6) 200deg, transparent 220deg)',
            animation: 'vcScanRotate 4s linear infinite',
          }} />
        </div>

        {/* Glass core */}
        <div style={{
          position: 'absolute',
          width: size * 0.75,
          height: size * 0.75,
          borderRadius: '50%',
          background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 50%, rgba(255, 255, 255, 0.05) 100%)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: 'inset 0 0 40px rgba(59, 130, 246, 0.1), 0 0 30px rgba(59, 130, 246, 0.2)',
        }} />

        {/* Glass highlight */}
        <div style={{
          position: 'absolute',
          width: size * 0.5,
          height: size * 0.2,
          top: size * 0.15,
          borderRadius: '50%',
          background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.15) 0%, transparent 100%)',
        }} />

        {/* Orbiting dot 1 */}
        <div style={{
          position: 'absolute',
          width: size,
          height: size,
          animation: 'vcOrbitSpin 8s linear infinite',
        }}>
          <div style={{
            position: 'absolute',
            top: -size * 0.025,
            left: '50%',
            transform: 'translateX(-50%)',
            width: size * 0.05,
            height: size * 0.05,
            background: '#3b82f6',
            borderRadius: '50%',
            boxShadow: '0 0 10px #3b82f6, 0 0 20px #3b82f6',
          }} />
        </div>

        {/* Orbiting dot 2 */}
        <div style={{
          position: 'absolute',
          width: size * 0.85,
          height: size * 0.85,
          animation: 'vcOrbitSpin 12s linear infinite reverse',
        }}>
          <div style={{
            position: 'absolute',
            top: -size * 0.015,
            left: '50%',
            transform: 'translateX(-50%)',
            width: size * 0.035,
            height: size * 0.035,
            background: '#60a5fa',
            borderRadius: '50%',
            boxShadow: '0 0 8px #60a5fa, 0 0 16px #60a5fa',
          }} />
        </div>

        {/* Scan line */}
        <div style={{
          position: 'absolute',
          width: '70%',
          height: '2px',
          background: 'linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.8), transparent)',
          animation: 'vcScanMove 3s ease-in-out infinite',
          filter: 'blur(1px)',
        }} />

        {/* VC Symbol */}
        <span style={{
          position: 'relative',
          zIndex: 20,
          fontSize: size * 0.32,
          fontWeight: 700,
          letterSpacing: '-1px',
          color: '#fff',
          textShadow: '0 0 20px rgba(59, 130, 246, 0.8), 0 0 40px rgba(59, 130, 246, 0.4), 0 0 60px rgba(59, 130, 246, 0.2)',
          animation: 'vcGlow 3s ease-in-out infinite',
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
        }}>
          VC
        </span>
      </div>

      {/* Text */}
      {showText && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: size * 0.02 }}>
          <span style={{
            fontSize: size * 0.4,
            fontWeight: 600,
            letterSpacing: size * 0.04,
            textTransform: 'uppercase',
            color: '#fff',
            textShadow: '0 0 20px rgba(59, 130, 246, 0.5), 0 0 40px rgba(59, 130, 246, 0.3)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
          }}>
            VinCentrix
          </span>
          <span style={{
            fontSize: size * 0.1,
            fontWeight: 500,
            letterSpacing: size * 0.02,
            textTransform: 'uppercase',
            color: 'rgba(255, 255, 255, 0.5)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
          }}>
            AI-Powered Acquisition
          </span>
        </div>
      )}

      <style>{`
        @keyframes vcCoreBreath {
          0%, 100% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.2); opacity: 0.8; }
        }
        @keyframes vcScanRotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes vcOrbitSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes vcScanMove {
          0%, 100% { top: 20%; opacity: 0; }
          10% { opacity: 1; }
          50% { top: 80%; opacity: 1; }
          60% { opacity: 0; }
        }
        @keyframes vcGlow {
          0%, 100% {
            text-shadow: 0 0 20px rgba(59, 130, 246, 0.8), 0 0 40px rgba(59, 130, 246, 0.4), 0 0 60px rgba(59, 130, 246, 0.2);
          }
          50% {
            text-shadow: 0 0 30px rgba(59, 130, 246, 1), 0 0 60px rgba(59, 130, 246, 0.6), 0 0 90px rgba(59, 130, 246, 0.3);
          }
        }
        @keyframes vcTechPulse {
          0%, 100% { transform: scale(1); border-color: rgba(59, 130, 246, 0.3); }
          50% { transform: scale(1.02); border-color: rgba(59, 130, 246, 0.6); }
        }
      `}</style>
    </div>
  );
};

export const VinCentrixIcon = ({ size = 90 }) => {
  return (
    <div style={{
      width: size,
      height: size,
      borderRadius: size * 0.22,
      background: 'linear-gradient(145deg, #0a0a10, #05050a)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden',
      border: '1px solid rgba(59, 130, 246, 0.3)',
      boxShadow: '0 0 30px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
    }}>
      {/* Rotating scanner */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'conic-gradient(from 0deg, transparent 0deg, rgba(59, 130, 246, 0.2) 30deg, transparent 60deg)',
        animation: 'vcIconScan 3s linear infinite',
      }} />

      {/* Inner glow */}
      <div style={{
        position: 'absolute',
        width: '60%',
        height: '60%',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(59, 130, 246, 0.2) 0%, transparent 70%)',
        animation: 'vcIconPulse 3s ease-in-out infinite',
      }} />

      {/* VC */}
      <span style={{
        position: 'relative',
        zIndex: 2,
        fontSize: size * 0.4,
        fontWeight: 700,
        color: '#fff',
        textShadow: '0 0 20px rgba(59, 130, 246, 0.8)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      }}>
        VC
      </span>

      <style>{`
        @keyframes vcIconScan {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes vcIconPulse {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.2); }
        }
      `}</style>
    </div>
  );
};

export default VinCentrixLogo;
